<template>
  <v-container id="dashboard" fluid tag="section" class="dashboard-home">
    <section class="dashboard-home__topbar">
      <section class="dashboard-home__tabs" role="tablist" aria-label="Dashboard tabs">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="dashboard-home__tab"
          :class="{ 'dashboard-home__tab--active': selectedTab === tab.id }"
          :aria-selected="selectedTab === tab.id"
          :tabindex="selectedTab === tab.id ? 0 : -1"
          @click="selectedTab = tab.id"
        >
          {{ tab.name }}
        </button>
        <div class="dashboard-home__search" role="search">
          <input
            v-model="searchQuery"
            type="search"
            class="dashboard-home__search-input"
            :placeholder="searchPlaceholder"
            autocomplete="off"
          />
          <v-icon class="dashboard-home__search-icon" aria-hidden="true">mdi-magnify</v-icon>
        </div>
      </section>
    </section>

    <v-card class="dashboard-home__card" flat>
      <div v-if="selectedTab === 0" class="dashboard-home__table-wrap">
        <v-data-table
          :headers="inspectionWorkOrdersHeaders"
          :items="inspectionWorkOrders"
          dense
          class="dashboard-home__table"
          :items-per-page="15"
          :loading="inspectionShowDataTableLoading"
          :loading-text="$t('SHARED.LBL_PLEASE_WAIT')"
          :no-data-text="$t('SHARED.LBL_NO_DATA')"
          :search="tableSearchQuery"
          :footer-props="{
            itemsPerPageText: $t('SHARED.LBL_ROWS_PER_PAGE'),
            itemsPerPageOptions: [5, 10, 15],
            pageText: '{0}-{1} ' + $t('SHARED.LBL_COUNT_OF_TOTAL') + ' {2}'
          }"
        >
          <template v-slot:[`item.actions`]="{ item }">
            <div class="actions-inline">
              <router-link
                :to="{ name: 'inspectionWorkOrder-details', params: { id: item.id } }"
                class="dashboard-home__action-link"
              >
                <v-icon small>mdi-details</v-icon>
              </router-link>
              <router-link
                :to="{ name: 'inspectionWorkOrder-edit', params: { id: item.id } }"
                class="dashboard-home__action-link"
              >
                <v-icon small>mdi-pencil</v-icon>
              </router-link>
            </div>
          </template>
        </v-data-table>
      </div>

      <div v-else-if="selectedTab === 1" class="dashboard-home__table-wrap">
        <v-data-table
          :headers="inspectionCaseHeaders"
          :items="inspectionCase"
          dense
          class="dashboard-home__table"
          :items-per-page="15"
          :loading="inspectionCaseShowDataLoading"
          :loading-text="$t('SHARED.LBL_PLEASE_WAIT')"
          :no-data-text="$t('SHARED.LBL_NO_DATA')"
          :search="tableSearchQuery"
          :footer-props="{
            itemsPerPageText: $t('SHARED.LBL_ROWS_PER_PAGE'),
            itemsPerPageOptions: [5, 10, 15],
            pageText: '{0}-{1} ' + $t('SHARED.LBL_COUNT_OF_TOTAL') + ' {2}'
          }"
        >
          <template v-slot:[`item.actions`]="{ item }">
            <div class="actions-inline">
              <router-link
                :to="{ name: 'inspectionWorkOrder-details', params: { id: item.itgInspectionWorkOrderId }, query: { step: 'Case', stepId: item.id } }"
                class="dashboard-home__action-link"
              >
                <v-icon small>mdi-details</v-icon>
              </router-link>
            </div>
          </template>
        </v-data-table>
      </div>

      <div v-else class="dashboard-home__table-wrap">
        <v-data-table
          :headers="ticketsHeaders"
          :items="tickets"
          dense
          class="dashboard-home__table"
          :items-per-page="15"
          :loading="ticketsShowDataTableLoading"
          :loading-text="$t('SHARED.LBL_PLEASE_WAIT')"
          :no-data-text="$t('SHARED.LBL_NO_DATA')"
          :search="tableSearchQuery"
          :footer-props="{
            itemsPerPageText: $t('SHARED.LBL_ROWS_PER_PAGE'),
            itemsPerPageOptions: [5, 10, 15],
            pageText: '{0}-{1} ' + $t('SHARED.LBL_COUNT_OF_TOTAL') + ' {2}'
          }"
        >
          <template v-slot:[`item.actions`]="{ item }">
            <div class="actions-inline">
              <router-link
                :to="{ name: 'ticket-details', params: { id: item.id } }"
                class="dashboard-home__action-link"
              >
                <v-icon small>mdi-details</v-icon>
              </router-link>
              <router-link
                v-if="item.status != 'Closed'"
                :to="{ name: 'ticket-edit', params: { id: item.id } }"
                class="dashboard-home__action-link"
              >
                <v-icon small>mdi-pencil</v-icon>
              </router-link>
            </div>
          </template>
        </v-data-table>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import TicketService from '../services/ITHelpDesk/ticket.service';
import InspectionProcedureService from '../services/inspectionProcedure.service';
import moment from 'moment';

export default {

  data() {
    return {
      tickets: [],
      inspectionWorkOrders: [],
      inspectionCase: [],
      // when true a preloader is shown for the datatable
      inspectionShowDataTableLoading: false,
      ticketsShowDataTableLoading: false,
      inspectionCaseShowDataLoading: false,
      selectedTab: 0,
      searchQuery: '',
      tabs:[
        { id: 0, name: this.$t('HOME.LBL_MY_INSPECTION_WORK_ORDERS') },
        { id: 1, name: this.$t('HOME.LBL_MY_INSPECTION_CASE') },
        { id: 2, name: this.$t('HOME.LBL_MY_IT_TICKETS') }
      ],
      alreadyLoadedTabContent: {
        inspectionCases: false,
        inspectionWorkOrders: false,
        tickets: false
      }

    }
  },
  computed: {
    ticketsHeaders() {
      return [
        {text: this.$t('IT_HELP_DESK.TICKETS.LBL_SUBJECT'), value: 'subject'},
        {text: this.$t('SHARED.LBL_STATUS'), value: 'status'},
        {text: this.$t('IT_HELP_DESK.TICKETS.LBL_PRIORITY'), value: 'priority', sortable: false},
        {text: this.$t('SHARED.LBL_ACTIONS'), value: 'actions', sortable: false},
      ]
    },
    inspectionWorkOrdersHeaders() {
      return [
        {text: this.$t('SUBJECTS.SUBJECT_SUBSIDIARIES.LBL_SUBJECT_SURVEILLANCE'), value: 'subjectName'},
        {text: this.$t('SUBJECTS.SUBJECT_SUBSIDIARIES.LBL_CHECK_TYPE'), value: 'checkType'},
        {text: this.$t('INSPECTION_PROCEDURE.INSPECTION_WORK_ORDERS.LBL_ARCHIVE_NUMBER'), value: 'archiveNumber'},
        {text: this.$t('INSPECTION_PROCEDURE.INSPECTION_WORK_ORDERS.LBL_WORK_ORDER_DATE'), value: 'inspectionCheckDate', sortable: false},
        {text: this.$t('SHARED.LBL_ACTIONS'), value: 'actions', sortable: false},
      ]
    },
    inspectionCaseHeaders() {
      return [
        {text: this.$t('INSPECTION_PROCEDURE.INSPECTION_CASES.LBL_SEQUENCE_NUMBER'), value: 'sequenceNumber'},
        {text: this.$t('INSPECTION_PROCEDURE.INSPECTION_CASES.LBL_ARCHIVE_NUMBER'), value: 'archiveNumber'},
        {text: this.$t('SHARED.LBL_NAME'), value: 'name'},
        {text: this.$t('SUBJECTS.SUBJECT_SUBSIDIARIES.LBL_SUBJECT_SURVEILLANCE'), value: 'subject.legalName'},
        {text: this.$t('SHARED.LBL_ACTIONS'), value: 'actions', sortable: false},
      ]
    },
    searchPlaceholder() {
      if (this.$te && this.$te('SHARED.LBL_SEARCH')) {
        return this.$t('SHARED.LBL_SEARCH');
      }
      return '\u041f\u0440\u0435\u0431\u0430\u0440\u0430\u0458';
    },
    tableSearchQuery() {
      return this.searchQuery ? this.searchQuery.trim() : '';
    },
    currentUser() {
      return this.$store.state.auth.user;
    }
  },
  created() {
    this.loadContentForSelectedTab(this.selectedTab);
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    }
  },
  methods: {
    getMytickets() {
      // show the preloader for the datatable
      this.ticketsShowDataTableLoading = true;
          return TicketService.getMyTickets()
              .then(response => {
                response.tickets.map(function (value) {
                  value.status = value.status != null ? this.$t('IT_HELP_DESK.TICKETS.TICKET_STATUS.' + value.status) : null;
                  value.priority = value.priority != null ? this.$t('IT_HELP_DESK.TICKETS.TICKET_PRIORITIES.' + value.priority) : null;
                }, this);
                this.tickets = response.tickets;
                this.alreadyLoadedTabContent.tickets = true;
              })
              .catch(err => window.console.log(err))
              // when the request is finished hide the preloader for the datatable
              .finally(() => this.ticketsShowDataTableLoading = false);

    },
    getMyInspectionWorkOrders() {
      // show the preloader for the datatable
      this.ticketsShowDataTableLoading = true;
          return InspectionProcedureService.getMyInspectionWorkOrders()
              .then(response => {
                response.inspectionWorkOrders.map(function (value) {
                  value.inspectionCheckDate = value.inspectionCheckDate != null ? moment(value.inspectionCheckDate).format('DD.MM.YYYY') : null;
                  value.subjectName = value.subject ? value.subject.legalName : null;
                  value.checkType = value.checkType != null ? this.$t('JBPM_PROCESS_FORM_FIELDS.INSPECTION_CASE_TYPES.' + value.checkType) : null;
                }, this);
                this.inspectionWorkOrders = response.inspectionWorkOrders;
                this.alreadyLoadedTabContent.inspectionWorkOrders = true;
              })
              .catch(err => window.console.log(err))
              // when the request is finished hide the preloader for the datatable
              .finally(() => this.inspectionShowDataTableLoading = false);
        },

    getMyInspectionCase() {
        this.inspectionCaseShowDataLoading = true;
        return InspectionProcedureService.getMyInspectionCasesList()
            .then(response => {
              response.inspectionCases.map(function (value) {
                value.subjectName = value.subjectName ? value.legalName : null;
              })
              this.inspectionCase = response.inspectionCases;
              this.alreadyLoadedTabContent.inspectionCases = true;
            }).catch(err => window.console.log(err))
            .finally(() => this.inspectionCaseShowDataLoading = false)
    },
    loadContentForSelectedTab(tabId) {
      switch (tabId) {
        default:
        case 0:
          if (!this.alreadyLoadedTabContent.inspectionWorkOrders)
            this.getMyInspectionWorkOrders();
          break;
        case 1:
          if (!this.alreadyLoadedTabContent.inspectionCases)
            this.getMyInspectionCase();
          break;
        case 2:
          if (!this.alreadyLoadedTabContent.tickets)
            this.getMytickets();
          break;
      }
    }
  },
  watch: {
    selectedTab(tabId) {
      this.loadContentForSelectedTab(tabId);
    }
  }
}
</script>

<style scoped>
.dashboard-home {
  max-width: 1360px;
  margin: 0 auto;
  padding: 26px 14px 34px;
}

.dashboard-home__topbar {
  margin: 4px 0 24px;
  max-width: 1088px;
  margin-left: auto;
  margin-right: auto;
}

.dashboard-home__tabs {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: nowrap;
  margin: 0;
  width: 100%;
}

.dashboard-home__tab {
  min-height: 62px;
  border: 0;
  border-radius: 30px;
  background: #1b3471;
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  padding: 14px 28px;
  transition: transform 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
  white-space: nowrap;
}

.dashboard-home__tab--active {
  background: linear-gradient(90deg, #b02c84 0%, #a72d83 100%);
  box-shadow: 0 8px 20px rgba(176, 44, 132, 0.24);
}

.dashboard-home__tab:hover {
  transform: translateY(-1px);
}

.dashboard-home__tab:focus-visible {
  outline: 3px solid rgba(219, 234, 254, 0.85);
  outline-offset: 2px;
}

.dashboard-home__search {
  position: relative;
  flex: 0 0 280px;
  max-width: 280px;
  min-width: 240px;
}

.dashboard-home__search-input {
  width: 100%;
  height: 54px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 400;
  padding: 0 52px 0 18px;
  box-shadow: 0 4px 10px rgba(30, 41, 59, 0.08);
  outline: none;
}

.dashboard-home__search-input::placeholder {
  color: #334155;
  opacity: 1;
}

.dashboard-home__search-input:focus {
  border-color: #1b3471;
  box-shadow: 0 0 0 3px rgba(27, 52, 113, 0.12);
}

.dashboard-home__search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #1b3471 !important;
  font-size: 30px !important;
}

.dashboard-home__card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 20px !important;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08) !important;
  overflow: hidden;
  max-width: 1088px;
  margin: 0 auto;
}

.dashboard-home__table-wrap {
  padding: 6px 10px 4px;
}

.dashboard-home__action-link {
  text-decoration: none;
}

.actions-inline {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.actions-inline .v-icon {
  color: #64748b !important;
}

.actions-inline .v-icon:hover {
  color: #1b3471 !important;
}

::v-deep .dashboard-home__table > .v-data-table__wrapper > table > thead > tr > th {
  color: #475569 !important;
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0;
  border-bottom: 1px solid #cbd5e1 !important;
}

::v-deep .dashboard-home__table > .v-data-table__wrapper > table > tbody > tr > td {
  color: #1e293b;
  font-size: 0.82rem;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 46px;
}

::v-deep .dashboard-home__table .v-data-footer {
  border-top: 0 !important;
  color: #475569;
  font-size: 0.8rem;
}

@media (max-width: 1260px) {
  .dashboard-home__tab {
    min-height: 52px;
    font-size: 0.96rem;
    padding: 12px 18px;
  }

  .dashboard-home__tabs {
    gap: 12px;
  }

  .dashboard-home__search {
    flex: 0 0 240px;
    max-width: 240px;
    min-width: 220px;
  }
}

@media (max-width: 960px) {
  .dashboard-home {
    padding: 18px 8px 24px;
  }

  .dashboard-home__tabs {
    gap: 10px;
    width: 100%;
  }

  .dashboard-home__tab {
    width: 100%;
    min-height: 50px;
    font-size: 0.92rem;
  }

  .dashboard-home__search-input {
    height: 50px;
    border-radius: 18px;
    font-size: 0.96rem;
  }

  .dashboard-home__search-icon {
    right: 14px;
    font-size: 26px !important;
  }

  .dashboard-home__table-wrap {
    padding: 6px;
  }
}
</style>

